import { Link } from '@tanstack/react-router';
import {
  CalendarDots,
  Desktop,
  DeviceMobile,
  DeviceTablet,
  Eye,
  Handshake,
  type Icon,
  MonitorPlay,
  SignIn,
  Sparkle,
  Storefront,
  Television,
} from '@/core/ui/icons';
import { Button, Card, cn, EmptyState, ErrorState, Skeleton } from '@/core/ui';
import { useActivityFeed, type ActivityFilters } from '../application/queries';
import {
  activityDetail,
  deviceLabel,
  exactTime,
  groupByDay,
  relativeTime,
  targetText,
  type Activity,
  type Category,
} from '../domain/activity';

const CATEGORY_ICONS: Record<Category, Icon> = {
  session: SignIn,
  navigation: Eye,
  learning: MonitorPlay,
  events: CalendarDots,
  products: Storefront,
  assistant: Sparkle,
  visits: Handshake,
};

const DEVICE_ICONS: Record<string, Icon> = {
  desktop: Desktop,
  mobile: DeviceMobile,
  tablet: DeviceTablet,
  tv: Television,
};

interface Props {
  filters: ActivityFilters;
  /** Na tela geral, quem fez cada coisa (com link para a pessoa); na página da pessoa, não. */
  showPerson?: boolean;
  emptyDescription?: string;
}

/**
 * Linha do tempo agrupada por dia, do mais recente para o mais antigo, com
 * "Carregar mais" no fim (a API pagina por cursor).
 */
export function ActivityTimeline({ filters, showPerson = false, emptyDescription }: Props) {
  const feed = useActivityFeed(filters);

  if (feed.isPending) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }
  if (feed.isError) return <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />;

  const items = feed.data.pages.flatMap((p) => p.items);
  if (items.length === 0) {
    return <EmptyState title="Nenhuma atividade" description={emptyDescription} />;
  }

  const now = new Date();
  return (
    <div className={cn('space-y-6 transition-opacity', feed.isPlaceholderData && 'opacity-60')}>
      {groupByDay(items, now).map((day) => (
        <section key={day.key} aria-label={day.label}>
          <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-subtle uppercase">{day.label}</h2>
          <Card>
            <ol className="divide-y divide-line">
              {day.items.map((a) => (
                <ActivityRow key={a.id} activity={a} showPerson={showPerson} now={now} />
              ))}
            </ol>
          </Card>
        </section>
      ))}

      {feed.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void feed.fetchNextPage()}
            disabled={feed.isFetchingNextPage}
          >
            {feed.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </Button>
        </div>
      )}
    </div>
  );
}

function ActivityRow({
  activity: a,
  showPerson,
  now,
}: {
  activity: Activity;
  showPerson: boolean;
  now: Date;
}) {
  const CategoryIcon = CATEGORY_ICONS[a.category as Category] ?? Eye;
  const target = targetText(a);
  const detail = activityDetail(a);
  const device = deviceLabel(a.device);
  const DeviceIcon = a.device ? (DEVICE_ICONS[a.device.kind] ?? Desktop) : null;
  // Página visitada com nome: o endereço vai como detalhe.
  const path = a.action === 'page.viewed' && a.target_label ? a.path : null;

  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <span
        aria-hidden
        className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-surface-2 text-fg-muted"
      >
        <CategoryIcon className="size-4" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-sm text-fg">
          {showPerson &&
            (a.user_id ? (
              <Link
                to="/pessoas/$userId"
                params={{ userId: a.user_id }}
                search={{ aba: 'atividade' }}
                className="font-semibold hover:underline"
              >
                {a.user_name ?? 'Pessoa sem nome'}
              </Link>
            ) : (
              <span className="font-semibold">Visitante</span>
            ))}
          {showPerson && ' '}
          <span className={showPerson ? 'text-fg-muted' : undefined}>
            {showPerson ? a.action_label.charAt(0).toLowerCase() + a.action_label.slice(1) : a.action_label}
          </span>
          {target && (
            <>
              {' '}
              <span className="font-medium">{target}</span>
            </>
          )}
        </p>
        {(detail || device || path) && (
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-fg-subtle">
            {detail && <span>{detail}</span>}
            {path && <span className="font-mono">{path}</span>}
            {device && DeviceIcon && (
              <span className="inline-flex items-center gap-1">
                <DeviceIcon aria-hidden className="size-3.5" />
                {device}
              </span>
            )}
          </p>
        )}
      </div>

      <time
        dateTime={a.occurred_at}
        title={exactTime(a.occurred_at)}
        className="shrink-0 pt-0.5 text-xs whitespace-nowrap text-fg-subtle tabular-nums"
      >
        {relativeTime(a.occurred_at, now)}
      </time>
    </li>
  );
}
